import {ConditionRelationType, ConditionTypeName, RelevantCondition, RelevantConditionRelations} from './types';
import uniqWith from 'lodash/uniqWith';

const conditionTypeOrder = [
  ConditionTypeName.Diagnoses,
  ConditionTypeName.PhysiologyAndLifeFactors,
  ConditionTypeName.Medications,
  ConditionTypeName.Procedures,
  ConditionTypeName.SymptomsAndFindings,
  ConditionTypeName.Tests,
  ConditionTypeName.Allergy,
  ConditionTypeName.FamilyHistory,
];

const conditionTypeColorsByName: {[key: string]: string} = {
  [ConditionTypeName.Diagnoses]: 'condition-diagnoses',
  [ConditionTypeName.SymptomsAndFindings]: 'condition-symptoms-and-findings',
  [ConditionTypeName.PhysiologyAndLifeFactors]: 'condition-physiology-and-life-factors',
  [ConditionTypeName.Medications]: 'condition-medications',
  [ConditionTypeName.FamilyHistory]: 'condition-family-history',
  [ConditionTypeName.Allergy]: 'condition-allergy',
  [ConditionTypeName.Tests]: 'condition-tests',
  [ConditionTypeName.Procedures]: 'condition-procedures',
  [ConditionTypeName.Category]: 'condition-category',
};

export const getOptionalRelatedRelevantConditions = (relevantConditions: RelevantCondition[]) => {
  return relevantConditions
    .filter((relevantCondition) => relevantCondition.conditionTypeName !== ConditionTypeName.Demographic)
    .filter((relevantCondition) => relevantCondition.conditionRelationType === ConditionRelationType.OptionalRelated)
    .sort(compareRelevantConditionsByType);
};

export const getPatientSettingRelevantConditions = (relevantConditions: RelevantCondition[]) => {
  return relevantConditions
    .filter((relevantCondition) => relevantCondition.conditionTypeName !== ConditionTypeName.Demographic)
    .filter((relevantCondition) => relevantCondition.isPatientSetting)
    .sort((a) => (a.conditionRelationType === ConditionRelationType.Selected ? -1 : 1))
    .filter((rc, index, array) => array.findIndex((rc2) => rc2.conditionId === rc.conditionId) === index);
};

export const getSelectedRelevantConditions = (relevantConditions: RelevantCondition[]) => {
  return relevantConditions
    .filter((relevantCondition) => relevantCondition.conditionTypeName !== ConditionTypeName.Demographic)
    .filter((relevantCondition) => relevantCondition.conditionRelationType === ConditionRelationType.Selected)
    .sort(compareRelevantConditionsByType);
};

export const getRequiredRelatedRelevantConditions = (relevantConditions: RelevantCondition[]) => {
  return relevantConditions
    .filter((relevantCondition) => relevantCondition.conditionTypeName !== ConditionTypeName.Demographic)
    .filter((relevantCondition) => relevantCondition.conditionRelationType === ConditionRelationType.RequiredRelated)
    .sort(compareRelevantConditionsByType);
};

export const isRelated = (rc1: RelevantCondition, rc2: RelevantCondition, crt: ConditionRelationType) =>
  (rc1.relatedConditionId === rc2.conditionId || rc1.relatedConditionIds?.includes(rc2.conditionId)) &&
  rc1.conditionRelationType === crt;

export const compareRelevantConditionsByType = (
  a: RelevantCondition | ConditionTypeName,
  b: RelevantCondition | ConditionTypeName,
) => {
  const aConditionTypeName = typeof a === 'object' ? a.conditionTypeName : a;
  const bConditionTypeName = typeof b === 'object' ? b.conditionTypeName : b;
  let aIndex = conditionTypeOrder.indexOf(aConditionTypeName);
  let bIndex = conditionTypeOrder.indexOf(bConditionTypeName);

  aIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
  bIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

  return aIndex - bIndex;
};

export const compareRelevantConditions = (
  relevantCondition1: RelevantCondition,
  relevantCondition2: RelevantCondition,
) => {
  return (
    Number(relevantCondition1.conditionRelationType === ConditionRelationType.RequiredRelated) -
      Number(relevantCondition2.conditionRelationType === ConditionRelationType.RequiredRelated) ||
    Number(relevantCondition1.showOrder ?? Number.MAX_SAFE_INTEGER) -
      Number(relevantCondition2.showOrder ?? Number.MAX_SAFE_INTEGER) ||
    relevantCondition1.conditionName.localeCompare(relevantCondition2.conditionName)
  );
};

export const dedupeRelevantConditions = (relevantConditions: RelevantCondition[]) => {
  return uniqWith(
    relevantConditions,
    (a, b) =>
      a.conditionId === b.conditionId &&
      a.conditionRelationType === b.conditionRelationType &&
      a.relatedConditionIds?.[0] === b.relatedConditionIds?.[0],
  );
};

export const getConditionTypeColorByName = (conditionTypeName: string) => {
  return conditionTypeColorsByName[conditionTypeName] ?? 'condition-other';
};

export const generateRelations = (relevantConditions: RelevantCondition[]): RelevantConditionRelations[] => {
  return relevantConditions.map((rc, _index, array) => {
    let childList = [];
    let optionalRelatedList = [];
    let requiredRelatedList = [];
    let suggestedList = [];

    for (const rc2 of array) {
      if (rc2.conditionId === rc.conditionId) {
        continue;
      }

      if (isRelated(rc2, rc, ConditionRelationType.Parent)) {
        childList.push(rc2);
        continue;
      }

      if (isRelated(rc2, rc, ConditionRelationType.OptionalRelated)) {
        optionalRelatedList.push(rc2);
        continue;
      }

      if (isRelated(rc2, rc, ConditionRelationType.RequiredRelated)) {
        requiredRelatedList.push(rc2);
        continue;
      }

      if (isRelated(rc2, rc, ConditionRelationType.Suggested)) {
        suggestedList.push(rc2);
        continue;
      }
    }

    return {
      childIds: childList.map((rc) => rc.conditionId),
      childList,
      optionalRelatedIds: optionalRelatedList.map((rc) => rc.conditionId),
      optionalRelatedList,
      relevantCondition: rc,
      requiredRelatedIds: requiredRelatedList.map((rc) => rc.conditionId),
      requiredRelatedList,
      suggestedIds: suggestedList.map((rc) => rc.conditionId),
      suggestedList,
    };
  });
};

export const contains = (
  relevantCondition1: RelevantCondition,
  relevantCondition2: RelevantCondition,
  relationsByConditionId: {[key: number]: RelevantConditionRelations},
  checkedRelevantConditions: RelevantCondition[] = [],
): boolean => {
  if (relevantCondition1.conditionId === relevantCondition2.conditionId) {
    return true;
  }

  if (checkedRelevantConditions.includes(relevantCondition1)) {
    return false;
  }

  checkedRelevantConditions.push(relevantCondition1);

  const {childList} = relationsByConditionId[relevantCondition1.conditionId];

  return childList.some((rc) => contains(rc, relevantCondition2, relationsByConditionId, checkedRelevantConditions));
};

export const hasRelation = (
  relevantCondition1: RelevantCondition,
  relevantCondition2: RelevantCondition,
  relationsByConditionId: {[key: number]: RelevantConditionRelations},
  checkedRelevantConditions: RelevantCondition[] = [],
): boolean => {
  if (relevantCondition1.conditionId === relevantCondition2.conditionId) {
    return true;
  }

  if (checkedRelevantConditions.includes(relevantCondition1)) {
    return false;
  }

  checkedRelevantConditions.push(relevantCondition1);

  if (checkedRelevantConditions.length === 1) {
    const {childIds, optionalRelatedIds, optionalRelatedList, suggestedIds} =
      relationsByConditionId[relevantCondition1.conditionId];

    if (childIds.includes(relevantCondition2.conditionId)) {
      return true;
    }

    if (optionalRelatedIds.includes(relevantCondition2.conditionId)) {
      return true;
    }

    if (suggestedIds.includes(relevantCondition2.conditionId)) {
      return true;
    }

    return optionalRelatedList
      .flatMap((rc) => relationsByConditionId[rc.conditionId].childList)
      .some((rc) => hasRelation(rc, relevantCondition2, relationsByConditionId, checkedRelevantConditions));
  } else {
    const {childList} = relationsByConditionId[relevantCondition1.conditionId];

    return childList.some((rc) =>
      hasRelation(rc, relevantCondition2, relationsByConditionId, checkedRelevantConditions),
    );
  }
};
